import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, MapPin, Trash2, Send, Copy, ExternalLink, ChevronUp, ChevronDown } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SP_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

function loadGoogleMaps() {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) return resolve(window.google);
    if (!MAPS_KEY) return resolve(null);
    if (document.getElementById("gmap-script")) {
      document.getElementById("gmap-script").addEventListener("load", () => resolve(window.google));
      return;
    }
    const s = document.createElement("script");
    s.id = "gmap-script";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places,geometry`;
    s.async = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => resolve(null);
    document.body.appendChild(s);
  });
}

// ------- List page -------
export function RoutesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapsStatus, setMapsStatus] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/routes"); setItems(data); } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    api.get("/config/maps-key").then((r) => setMapsStatus(r.data)).catch(() => {});
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    try { await api.delete(`/routes/${id}`); setItems((p) => p.filter((r) => r.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> Back to dashboard</Link>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Sales routes</h1>
            <p className="text-slate-500 mt-2">Split addresses between your salespeople and send each a shareable route link.</p>
          </div>
          <Link to="/routes/new"><Button data-testid="new-route-btn"><Plus size={16} className="mr-1.5" /> New Route</Button></Link>
          <Link to="/routes/analytics" className="ml-2"><Button variant="outline" data-testid="analytics-btn">Analytics</Button></Link>
        </div>
        {!MAPS_KEY && (
          <Card className="p-4 rounded-md border-amber-200 bg-amber-50 mb-4 text-sm text-amber-900" data-testid="maps-key-warning">
            <b>Google Maps API key not configured.</b> Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>/app/frontend/.env</code> and <code>GOOGLE_MAPS_API_KEY</code> in <code>/app/backend/.env</code>, then restart. Routes still work as lists; maps and geocoding activate once the key is added.
          </Card>
        )}
        {MAPS_KEY && mapsStatus && !mapsStatus.ok && (
          <Card className="p-4 rounded-md border-amber-200 bg-amber-50 mb-4 text-sm text-amber-900" data-testid="maps-billing-warning">
            <b>Google Maps key needs billing enabled.</b> Your key is set, but Google returned <code>{mapsStatus.status}</code>.{" "}
            {mapsStatus.error_message && <span className="block mt-1 text-amber-800">{mapsStatus.error_message}</span>}
            Enable billing at <a className="underline" href="https://console.cloud.google.com/project/_/billing/enable" target="_blank" rel="noreferrer">console.cloud.google.com/billing</a>. Routes still work as lists — maps and geocoding will activate once billing is on.
          </Card>
        )}
        {loading ? <p className="text-slate-500 text-sm">Loading…</p> :
         items.length === 0 ? (
          <Card className="p-10 rounded-md border-slate-200 shadow-sm text-center"><p className="text-slate-500">No routes yet. Create one to get started.</p></Card>
         ) : (
          <div className="space-y-3" data-testid="routes-list">
            {items.map((r) => (
              <Card key={r.id} className="p-4 rounded-md border-slate-200 shadow-sm flex items-center justify-between" data-testid={`route-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/routes/${r.id}`} className="text-slate-900 font-semibold hover:underline">{r.name}</Link>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${r.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{r.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{r.stops.length} stops · {r.salespeople.length} salespeople</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/routes/${r.id}`}><Button variant="outline" size="sm">Open</Button></Link>
                  <Button variant="outline" size="sm" className="text-rose-700" onClick={() => remove(r.id)} data-testid={`route-delete-${r.id}`}><Trash2 size={13} /></Button>
                </div>
              </Card>
            ))}
          </div>
         )}
      </main>
    </div>
  );
}

// ------- New route -------
export function NewRoute() {
  const [name, setName] = useState("");
  const [addressesText, setAddressesText] = useState("");
  const [pins, setPins] = useState([]);   // [{id, lat, lng, formatted}]
  const [sp, setSp] = useState([{ name: "", email: "" }]);
  const [splitMethod, setSplitMethod] = useState("geo");
  const [existingAssignees, setExistingAssignees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapsStatus, setMapsStatus] = useState(null);

  useEffect(() => {
    api.get("/assignees").then((r) => setExistingAssignees(r.data)).catch(() => {});
    api.get("/config/maps-key").then((r) => setMapsStatus(r.data)).catch(() => {});
  }, []);

  // ---- Map picker: click to drop pins, autocomplete search, drag to nudge ----
  useEffect(() => {
    if (!MAPS_KEY) return;
    let map, geocoder, markers = [];
    let cancelled = false;
    (async () => {
      const google = await loadGoogleMaps();
      if (!google || cancelled) return;
      geocoder = new google.maps.Geocoder();
      map = new google.maps.Map(document.getElementById("picker-map"), {
        zoom: 11, center: { lat: 12.9716, lng: 77.5946 }, streetViewControl: false, mapTypeControl: false,
      });
      setMapReady(true);

      const addPinFromLatLng = (latLng, formatted) => {
        const id = Math.random().toString(36).slice(2, 8);
        const stop = { id, lat: latLng.lat(), lng: latLng.lng(), formatted: formatted || `${latLng.lat().toFixed(5)}, ${latLng.lng().toFixed(5)}` };
        setPins((prev) => [...prev, stop]);
        return stop;
      };
      const reverseGeocode = (latLng) => new Promise((resolve) => {
        geocoder.geocode({ location: latLng }, (results, status) => {
          resolve(status === "OK" && results?.[0]?.formatted_address);
        });
      });

      map.addListener("click", async (e) => {
        const formatted = await reverseGeocode(e.latLng);
        addPinFromLatLng(e.latLng, formatted);
      });

      // Autocomplete search box
      const input = document.getElementById("picker-search");
      if (input && google.maps.places) {
        const ac = new google.maps.places.Autocomplete(input, { fields: ["formatted_address", "geometry", "name"] });
        ac.bindTo("bounds", map);
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place.geometry) return;
          const loc = place.geometry.location;
          map.panTo(loc); map.setZoom(14);
          addPinFromLatLng(loc, place.formatted_address || place.name);
          input.value = "";
        });
      }

      // Re-render markers when pins change (via a MutationObserver-free approach:
      // we cheat and expose a redraw fn on window, then react effect calls it below)
      window.__pickerRedraw = (currentPins) => {
        markers.forEach((m) => m.setMap(null));
        markers = [];
        currentPins.forEach((p, i) => {
          const pos = { lat: p.lat, lng: p.lng };
          const m = new google.maps.Marker({
            position: pos, map, draggable: true,
            label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
            icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: "#0f172a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 12 },
          });
          m.addListener("dragend", async (e) => {
            const formatted = await reverseGeocode(e.latLng);
            setPins((prev) => prev.map((x) => x.id === p.id ? { ...x, lat: e.latLng.lat(), lng: e.latLng.lng(), formatted: formatted || x.formatted } : x));
          });
          m.addListener("click", () => {
            setPins((prev) => prev.filter((x) => x.id !== p.id));
          });
          markers.push(m);
        });
        // Note: intentionally NOT calling map.fitBounds here — auto-fit after every click
        // would re-zoom the map and disorient the admin mid-drop. Autocomplete pans/zooms
        // via map.panTo + setZoom, which is enough visual feedback.
      };
    })();
    return () => { cancelled = true; window.__pickerRedraw = undefined; };
  }, []);

  useEffect(() => {
    if (window.__pickerRedraw) window.__pickerRedraw(pins);
  }, [pins]);

  const removePin = (id) => setPins((p) => p.filter((x) => x.id !== id));
  const movePin = (idx, dir) => setPins((p) => {
    const j = idx + dir;
    if (j < 0 || j >= p.length) return p;
    const copy = [...p];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  });

  const addSp = () => setSp((p) => [...p, { name: "", email: "" }]);
  const removeSp = (i) => setSp((p) => p.filter((_, idx) => idx !== i));
  const pickExisting = (i, email) => {
    const a = existingAssignees.find((x) => x.email === email);
    if (a) setSp((p) => p.map((x, idx) => (idx === i ? { name: a.name, email: a.email } : x)));
  };

  const submit = async () => {
    // Union of pin-picked addresses (ordered first) + textarea lines
    const pinAddrs = pins.map((p) => p.formatted);
    const textAddrs = addressesText.split("\n").map((s) => s.trim()).filter(Boolean);
    const addresses = [...pinAddrs, ...textAddrs.filter((a) => !pinAddrs.includes(a))];
    if (!name.trim()) return toast.error("Name the route");
    if (addresses.length === 0) return toast.error("Drop a pin on the map or paste at least one address");
    const salespeople = sp.filter((x) => x.name.trim() && x.email.trim());
    if (salespeople.length === 0) return toast.error("Add at least one salesperson");
    setBusy(true);
    try {
      const { data } = await api.post("/routes", { name, addresses, salespeople, split_method: splitMethod });
      toast.success("Route created — review and send");
      window.location.href = `/routes/${data.id}`;
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const billingBlocked = MAPS_KEY && mapsStatus && !mapsStatus.ok;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/routes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> Back to routes</Link>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>New route</h1>

        <Card className="p-5 rounded-md border-slate-200 shadow-sm space-y-5">
          <div className="space-y-1.5">
            <Label>Route name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. North zone — Tuesday" data-testid="route-name-input" />
          </div>

          {/* Address Picker */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <Label>Address picker</Label>
              <span className="text-xs text-slate-500">Click map to drop · drag to nudge · click a pin to remove</span>
            </div>
            {!MAPS_KEY && (
              <div className="p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900" data-testid="picker-no-key">
                Map picker needs a Google Maps API key. Paste addresses in the textarea below for now.
              </div>
            )}
            {billingBlocked && (
              <div className="p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-900" data-testid="picker-billing">
                Google returned <code>{mapsStatus.status}</code>. Enable billing to unlock the map picker; the textarea below still works.
              </div>
            )}
            {MAPS_KEY && (
              <>
                <Input id="picker-search" placeholder="Search a place to add it as a pin…" className="h-10" data-testid="picker-search-input" />
                <div id="picker-map" className="w-full h-72 rounded border border-slate-200 bg-slate-100" data-testid="picker-map" />
                {pins.length > 0 && (
                  <ol className="space-y-1.5 mt-2" data-testid="pin-list">
                    {pins.map((p, i) => (
                      <li key={p.id} className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-slate-50/50" data-testid={`pin-${p.id}`}>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-semibold">{i + 1}</span>
                        <span className="flex-1 text-sm text-slate-800 truncate">{p.formatted}</span>
                        <button className="p-1 text-slate-500 hover:text-slate-900" onClick={() => movePin(i, -1)} title="Up"><ChevronUp size={12} /></button>
                        <button className="p-1 text-slate-500 hover:text-slate-900" onClick={() => movePin(i, 1)} title="Down"><ChevronDown size={12} /></button>
                        <button className="p-1 text-rose-500 hover:text-rose-700" onClick={() => removePin(p.id)} title="Remove" data-testid={`pin-rm-${p.id}`}><Trash2 size={12} /></button>
                      </li>
                    ))}
                  </ol>
                )}
                {MAPS_KEY && mapReady && pins.length === 0 && (
                  <p className="text-xs text-slate-500 italic">No pins yet — click anywhere on the map or use the search box above.</p>
                )}
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Or paste addresses (one per line)</Label>
            <Textarea rows={5} value={addressesText} onChange={(e) => setAddressesText(e.target.value)} placeholder={"123 Main St, Bengaluru\n45 MG Rd, Bengaluru\n…"} data-testid="route-addresses-input" />
            <p className="text-xs text-slate-500">Pasted addresses will be added after the pins above; duplicates are removed.</p>
          </div>

          <div className="space-y-2">
            <Label>Salespeople ({sp.length})</Label>
            {sp.map((x, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`sp-row-${i}`}>
                <Input placeholder="Name" value={x.name} onChange={(e) => setSp((p) => p.map((y, idx) => idx === i ? { ...y, name: e.target.value } : y))} data-testid={`sp-name-${i}`} />
                <Input type="email" placeholder="Email" value={x.email} onChange={(e) => setSp((p) => p.map((y, idx) => idx === i ? { ...y, email: e.target.value } : y))} data-testid={`sp-email-${i}`} />
                <Select onValueChange={(v) => pickExisting(i, v)}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Or pick existing…" /></SelectTrigger>
                  <SelectContent>{existingAssignees.map((a) => <SelectItem key={a.email} value={a.email}>{a.name} ({a.email})</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => removeSp(i)}><Trash2 size={13} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addSp} data-testid="add-sp-btn"><Plus size={13} className="mr-1" /> Add salesperson</Button>
          </div>
          <div className="space-y-1.5">
            <Label>Split method</Label>
            <Select value={splitMethod} onValueChange={setSplitMethod}>
              <SelectTrigger className="h-10" data-testid="split-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="geo">Geographic clusters (recommended)</SelectItem>
                <SelectItem value="count">Equal count in listed order</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Link to="/routes"><Button type="button" variant="outline">Cancel</Button></Link>
            <Button type="button" onClick={submit} disabled={busy} data-testid="route-create-submit">{busy ? "Creating…" : "Create draft"}</Button>
          </div>
        </Card>
      </main>
    </div>
  );
}

// ------- Editor -------
export function RouteEditor() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerTarget, setPickerTarget] = useState("");   // salesperson email that receives new stops
  const [mapsStatus, setMapsStatus] = useState(null);

  // Refs so the map's click/autocomplete handlers always see the latest state
  // without re-instantiating the map on every change.
  const routeRef = useRef(null);
  const pickerTargetRef = useRef("");
  useEffect(() => { routeRef.current = route; }, [route]);
  useEffect(() => { pickerTargetRef.current = pickerTarget; }, [pickerTarget]);

  const load = async () => {
    try { const { data } = await api.get(`/routes/${id}`); setRoute(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/config/maps-key").then((r) => setMapsStatus(r.data)).catch(() => {}); }, []);

  // Default the picker target to the first salesperson once the route loads
  useEffect(() => {
    if (route && !pickerTarget && route.salespeople.length > 0) {
      setPickerTarget(route.salespeople[0].email);
    }
  }, [route, pickerTarget]);

  // Adds a stop (from a map click or autocomplete result) to the current picker target
  const addStopAt = async (lat, lng, formatted) => {
    const r = routeRef.current;
    const targetEmail = pickerTargetRef.current;
    if (!r || !targetEmail) return;
    const newStop = {
      id: Math.random().toString(36).slice(2, 10),
      address: formatted || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      formatted: formatted || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      lat, lng, notes: "",
    };
    const stops = [...r.stops, newStop];
    const plan = r.plan.map((p) =>
      p.email === targetEmail ? { ...p, stop_ids: [...p.stop_ids, newStop.id] } : p
    );
    try {
      const { data } = await api.patch(`/routes/${id}`, { stops, plan });
      setRoute(data);
      toast.success(`Added stop for ${r.salespeople.find((s) => s.email === targetEmail)?.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!route || !MAPS_KEY) return;
    let cancelled = false;
    (async () => {
      const google = await loadGoogleMaps();
      if (!google || cancelled) return;
      const map = new google.maps.Map(document.getElementById("editor-map"), {
        zoom: 11, center: { lat: 12.9716, lng: 77.5946 }, streetViewControl: false, mapTypeControl: false,
      });
      const geocoder = new google.maps.Geocoder();
      const bounds = new google.maps.LatLngBounds();
      route.plan.forEach((entry, spIdx) => {
        const color = SP_COLORS[spIdx % SP_COLORS.length];
        entry.stop_ids.forEach((sid, i) => {
          const s = route.stops.find((x) => x.id === sid);
          if (!s || s.lat == null) return;
          const pos = { lat: s.lat, lng: s.lng };
          new google.maps.Marker({
            position: pos, map,
            label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
            icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 12 },
          });
          bounds.extend(pos);
        });
        const encoded = route.polylines && route.polylines[entry.email];
        if (encoded && google.maps.geometry) {
          const path = google.maps.geometry.encoding.decodePath(encoded);
          new google.maps.Polyline({
            path, geodesic: true, strokeColor: color, strokeOpacity: 0.85, strokeWeight: 4, map,
          });
          path.forEach((p) => bounds.extend(p));
        }
      });
      if (!bounds.isEmpty()) map.fitBounds(bounds);

      // Click map → reverse-geocode → add as a new stop for the current target
      map.addListener("click", (e) => {
        geocoder.geocode({ location: e.latLng }, (results, status) => {
          const formatted = status === "OK" && results?.[0]?.formatted_address;
          addStopAt(e.latLng.lat(), e.latLng.lng(), formatted);
        });
      });

      // Autocomplete search → add pin from a searched place
      const input = document.getElementById("editor-search");
      if (input && google.maps.places) {
        const ac = new google.maps.places.Autocomplete(input, { fields: ["formatted_address", "geometry", "name"] });
        ac.bindTo("bounds", map);
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place.geometry) return;
          const loc = place.geometry.location;
          map.panTo(loc); map.setZoom(14);
          addStopAt(loc.lat(), loc.lng(), place.formatted_address || place.name);
          input.value = "";
        });
      }
    })();
    return () => { cancelled = true; };
  }, [route]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/routes/${id}`, updates);
      setRoute(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const moveStop = (spEmail, idx, direction) => {
    const plan = route.plan.map((p) => ({ ...p, stop_ids: [...p.stop_ids] }));
    const entry = plan.find((p) => p.email === spEmail);
    const j = idx + direction;
    if (j < 0 || j >= entry.stop_ids.length) return;
    [entry.stop_ids[idx], entry.stop_ids[j]] = [entry.stop_ids[j], entry.stop_ids[idx]];
    patch({ plan });
  };
  const removeStop = (spEmail, stopId) => {
    const plan = route.plan.map((p) => ({ ...p, stop_ids: p.stop_ids.filter((x) => x !== stopId) }));
    patch({ plan });
  };
  const reassignStop = (fromEmail, toEmail, stopId) => {
    const plan = route.plan.map((p) => {
      if (p.email === fromEmail) return { ...p, stop_ids: p.stop_ids.filter((x) => x !== stopId) };
      if (p.email === toEmail) return { ...p, stop_ids: [...p.stop_ids, stopId] };
      return p;
    });
    patch({ plan });
  };

  // ---- drag & drop across panels ----
  const [dragOver, setDragOver] = useState(null);  // { email, index } | null
  const onDragStart = (e, fromEmail, stopId) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ fromEmail, stopId }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropStop = (e, toEmail, insertIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("application/json")); } catch { return; }
    if (!payload) return;
    const { fromEmail, stopId } = payload;
    const plan = route.plan.map((p) => ({ ...p, stop_ids: [...p.stop_ids] }));
    // remove from source
    const src = plan.find((p) => p.email === fromEmail);
    if (!src) return;
    const currentIdx = src.stop_ids.indexOf(stopId);
    if (currentIdx === -1) return;
    src.stop_ids.splice(currentIdx, 1);
    const dst = plan.find((p) => p.email === toEmail);
    if (!dst) return;
    let idx = insertIndex;
    // if same panel and removing shifted indices left, adjust
    if (fromEmail === toEmail && currentIdx < insertIndex) idx = insertIndex - 1;
    idx = Math.max(0, Math.min(idx, dst.stop_ids.length));
    dst.stop_ids.splice(idx, 0, stopId);
    patch({ plan });
  };

  const optimize = async () => {
    try {
      const { data } = await api.post(`/routes/${id}/optimize`);
      setRoute(data);
      toast.success(`Optimized ${data.optimized ?? 0} route${(data.optimized ?? 0) === 1 ? "" : "s"} by driving time`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendAll = async () => {
    if (!window.confirm("Send route links to all salespeople now?")) return;
    try {
      const { data } = await api.post(`/routes/${id}/send`);
      toast.success(`Sent to ${data.sent.filter((s) => s.sent).length} of ${data.sent.length}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/route/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Route link copied"); } catch {}
  };

  if (loading) return <div className="min-h-screen bg-slate-50"><Navbar /><main className="max-w-5xl mx-auto px-6 py-8"><p className="text-slate-500">Loading…</p></main></div>;
  if (!route) return null;
  const stopById = Object.fromEntries(route.stops.map((s) => [s.id, s]));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/routes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> Back to routes</Link>
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{route.name}</h1>
            <p className="text-slate-500 mt-1 text-sm">{route.stops.length} stops · {route.salespeople.length} salespeople · {route.status}</p>
          </div>
          <Button onClick={sendAll} data-testid="send-route-btn"><Send size={14} className="mr-1.5" /> Send links to all</Button>
          <Button variant="outline" onClick={optimize} data-testid="optimize-route-btn"><MapPin size={14} className="mr-1.5" /> Optimize order</Button>
        </div>

        {MAPS_KEY ? (
          <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden mb-6">
            <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/50">
              <span className="text-xs font-medium text-slate-600 shrink-0">Add stops:</span>
              <Select value={pickerTarget} onValueChange={setPickerTarget}>
                <SelectTrigger className="h-8 w-56 text-sm" data-testid="editor-picker-target">
                  <SelectValue placeholder="Choose salesperson" />
                </SelectTrigger>
                <SelectContent>
                  {route.salespeople.map((sp) => (
                    <SelectItem key={sp.email} value={sp.email}>{sp.name} ({sp.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input id="editor-search" placeholder="Search a place to add as a stop…" className="h-8 text-sm flex-1 min-w-[220px]" data-testid="editor-search-input" />
              <span className="text-xs text-slate-500">or click the map</span>
            </div>
            {mapsStatus && !mapsStatus.ok && (
              <div className="px-3 py-2 border-b border-slate-100 bg-amber-50 text-xs text-amber-900" data-testid="editor-map-billing-warning">
                Google returned <code>{mapsStatus.status}</code>. Map click still drops a pin (lat/lng shown as the address); enable billing for reverse-geocoded street names.
              </div>
            )}
            <div id="editor-map" style={{ width: "100%", height: 400 }} data-testid="editor-map" />
          </Card>
        ) : (
          <Card className="p-4 rounded-md border-amber-200 bg-amber-50 mb-6 text-sm text-amber-900">Add a Google Maps API key to see the overview map.</Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {route.plan.map((entry, spIdx) => {
            const sp = route.salespeople.find((s) => s.email === entry.email);
            const color = SP_COLORS[spIdx % SP_COLORS.length];
            return (
              <Card
                key={entry.email}
                className="p-4 rounded-md border-slate-200 shadow-sm"
                data-testid={`sp-panel-${entry.email}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => onDropStop(e, entry.email, entry.stop_ids.length)}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
                    <div>
                      <div className="font-semibold text-slate-900">{sp?.name}</div>
                      <div className="text-xs text-slate-500">{sp?.email} · {entry.stop_ids.length} stops</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyLink(sp?.share_token)} data-testid={`copy-route-link-${entry.email}`}><Copy size={12} className="mr-1" /> Copy link</Button>
                </div>
                {entry.stop_ids.length === 0 && (
                  <div className="text-xs text-slate-400 italic py-6 text-center border border-dashed border-slate-200 rounded" data-testid={`empty-drop-${entry.email}`}>
                    Drop a stop here to assign it to {sp?.name}
                  </div>
                )}
                <ol className="space-y-1.5">
                  {entry.stop_ids.map((sid, i) => {
                    const s = stopById[sid];
                    if (!s) return null;
                    const isOverBefore = dragOver && dragOver.email === entry.email && dragOver.index === i;
                    return (
                      <li
                        key={sid}
                        draggable
                        onDragStart={(e) => onDragStart(e, entry.email, sid)}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver({ email: entry.email, index: i }); }}
                        onDragLeave={() => setDragOver(null)}
                        onDrop={(e) => onDropStop(e, entry.email, i)}
                        className={`flex items-start gap-2 p-2 rounded border bg-slate-50/50 cursor-grab active:cursor-grabbing transition-colors ${isOverBefore ? "border-slate-900 bg-slate-100" : "border-slate-200"}`}
                        data-testid={`stop-${sid}`}
                      >
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-semibold shrink-0" style={{ background: color }}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-800 truncate">{s.formatted || s.address}</div>
                          {s.lat == null && <div className="text-[10px] text-amber-700">Not geocoded — needs Maps billing on</div>}
                        </div>
                        <div className="flex items-center gap-0.5">
                          <button className="p-1 text-slate-500 hover:text-slate-900" onClick={() => moveStop(entry.email, i, -1)} title="Up" data-testid={`up-${sid}`}><ChevronUp size={12} /></button>
                          <button className="p-1 text-slate-500 hover:text-slate-900" onClick={() => moveStop(entry.email, i, 1)} title="Down" data-testid={`down-${sid}`}><ChevronDown size={12} /></button>
                          <button className="p-1 text-rose-500 hover:text-rose-700" onClick={() => removeStop(entry.email, sid)} title="Remove" data-testid={`rm-${sid}`}><Trash2 size={12} /></button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </Card>
            );
          })}
        </div>
        {saving && <p className="text-xs text-slate-500 mt-3">Saving…</p>}
      </main>
    </div>
  );
}

// ------- Public route (salesperson view) -------
export function PublicRoute() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/routes/${token}`);
        if (res.status === 404) { setNotFound(true); return; }
        const json = await res.json();
        setData(json);
      } catch {} finally { setLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    if (!data || !MAPS_KEY) return;
    (async () => {
      const google = await loadGoogleMaps();
      if (!google) return;
      const map = new google.maps.Map(document.getElementById("public-map"), { zoom: 12, center: { lat: 12.9716, lng: 77.5946 } });
      const bounds = new google.maps.LatLngBounds();
      const fallbackPath = [];
      data.stops.forEach((s, i) => {
        if (s.lat == null) return;
        const pos = { lat: s.lat, lng: s.lng };
        fallbackPath.push(pos);
        new google.maps.Marker({
          position: pos, map,
          label: { text: String(i + 1), color: "#fff", fontSize: "12px", fontWeight: "700" },
          icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: "#0f172a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 14 },
        });
        bounds.extend(pos);
      });
      // Prefer the real driving polyline from Directions if we have it; else straight-line fallback.
      if (data.polyline && google.maps.geometry) {
        const path = google.maps.geometry.encoding.decodePath(data.polyline);
        new google.maps.Polyline({ path, geodesic: true, strokeColor: "#0f172a", strokeOpacity: 0.85, strokeWeight: 4, map });
        path.forEach((p) => bounds.extend(p));
      } else if (fallbackPath.length > 1) {
        new google.maps.Polyline({ path: fallbackPath, geodesic: true, strokeColor: "#0f172a", strokeOpacity: 0.5, strokeWeight: 3, map });
      }
      if (!bounds.isEmpty()) map.fitBounds(bounds);
    })();
  }, [data]);

  if (loading) return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-500 text-sm">Loading…</div>;
  if (notFound) return <div className="min-h-screen bg-slate-50 grid place-items-center px-6"><Card className="p-8 max-w-md text-center"><h1 className="text-2xl font-semibold">Route not found</h1></Card></div>;
  if (!data) return null;

  const startNav = () => {
    const geo = data.stops.filter((s) => s.lat != null);
    if (geo.length === 0) return toast.error("No geocoded stops yet");
    const origin = `${geo[0].lat},${geo[0].lng}`;
    const dest = `${geo[geo.length - 1].lat},${geo[geo.length - 1].lng}`;
    const waypoints = geo.slice(1, -1).map((s) => `${s.lat},${s.lng}`).join("|");
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}` + (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "") + "&travelmode=driving";
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-slate-900 text-white grid place-items-center"><MapPin size={16} /></div>
          <div>
            <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{data.route_name}</div>
            <div className="text-xs text-slate-500">for {data.salesperson.name}</div>
          </div>
          <Button onClick={startNav} className="ml-auto" data-testid="start-nav-btn"><MapPin size={14} className="mr-1.5" /> Start Navigation</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-6">
        {MAPS_KEY ? (
          <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden mb-4">
            <div id="public-map" style={{ width: "100%", height: 360 }} data-testid="public-map" />
          </Card>
        ) : (
          <Card className="p-4 rounded-md border-amber-200 bg-amber-50 mb-4 text-sm text-amber-900">Map preview requires a Google Maps API key.</Card>
        )}
        <ol className="space-y-2" data-testid="public-stops">
          {data.stops.map((s, i) => (
            <li key={s.id} className="p-3 rounded-md border border-slate-200 bg-white flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{s.formatted || s.address}</div>
                {s.notes && <div className="text-xs text-slate-500 mt-0.5">{s.notes}</div>}
              </div>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.formatted || s.address)}`} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900" title="Open in Maps"><ExternalLink size={14} /></a>
            </li>
          ))}
        </ol>
      </main>
    </div>
  );
}
